import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "expired", labelKey: "exp.expiredB", cls: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-600" },
  { key: "today", labelKey: "exp.today", cls: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500" },
  { key: "tomorrow", labelKey: "exp.tomorrow", cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  { key: "3days", labelKey: "exp.in3", cls: "bg-yellow-50 text-yellow-800 border-yellow-200", dot: "bg-yellow-500" },
  { key: "7days", labelKey: "exp.in7", cls: "bg-slate-50 text-slate-700 border-slate-200", dot: "bg-slate-400" },
];

export default function ExpirationsPage() {
  const { locationId } = useAuth();
  const user = useUser();
  const { t } = useLang();
  const [groups, setGroups] = useState(null);
  const canSeeCosts = ["owner", "manager", "chef"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/expirations", { params: { location_id: locationId } })
      .then((r) => setGroups(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [load]);

  if (!groups) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 md:pl-14" data-testid="expirations-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("exp.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("exp.fefoNote")}</p>
      </div>

      <div className="space-y-4">
        {BUCKETS.map(({ key, labelKey, cls, dot }) => (
          <section key={key} data-testid={`fefo-group-${key}`} className={`rounded-2xl border-2 p-4 ${cls}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
              <h2 className="font-display font-bold">{t(labelKey)}</h2>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/60">{groups[key].length}</span>
            </div>
            <div className="space-y-2">
              {groups[key].map((p) => (
                <div key={p.product_id} data-testid={`fefo-item-${p.product_id}`}
                  className="bg-card rounded-xl border p-3 flex items-center gap-3 flex-wrap">
                  <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-32">
                    <p className="font-semibold text-sm">{p.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{p.expiry_date}</p>
                  </div>
                  <p className="font-mono text-sm font-bold">{p.current_qty} {p.unit}</p>
                  {canSeeCosts && p.value > 0 && (
                    <p className="text-xs font-mono text-muted-foreground">€{p.value.toFixed(2)}</p>
                  )}
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-secondary">
                    {p.days_left <= 0 ? t("exp.expiredB") : `${p.days_left} ${t("exp.days")}`}
                  </span>
                </div>
              ))}
              {!groups[key].length && (
                <p className="text-xs opacity-70 py-1">{t("exp.noItems")}</p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
